// Firebase Authentication Handler using modern SDK
// This module handles Firebase auth state and syncs with backend

(function() {
    'use strict';
    
    // Wait for Firebase to be available
    function waitForFirebase() {
        return new Promise((resolve) => {
            let attempts = 0;
            const checkFirebase = setInterval(() => {
                attempts++;
                
                // Check if Firebase is loaded (from app.js bundle)
                if (typeof firebase !== 'undefined' && firebase.auth) {
                    clearInterval(checkFirebase);
                    resolve(firebase);
                } else if (attempts > 50) { // 5 seconds timeout
                    clearInterval(checkFirebase);
                    console.error('[FirebaseAuth] Firebase not available after 5 seconds');
                    resolve(null);
                }
            }, 100);
        });
    }
    
    // Sync user with backend
    async function syncUserWithBackend(user) {
        if (!user) {
            console.log('[FirebaseAuth] No user to sync');
            return false;
        }
        
        try {
            console.log('[FirebaseAuth] Syncing user with backend:', user.uid);
            
            // Get the provider ID (google.com, apple.com, or password)
            const providerId = user.providerData && user.providerData.length > 0 
                ? user.providerData[0].providerId 
                : 'password';
            
            // Get the Firebase ID token for server-side verification
            let idToken = null;
            try {
                idToken = await user.getIdToken();
            } catch (tokenError) {
                console.warn('[FirebaseAuth] Could not get ID token:', tokenError);
            }
            
            const response = await fetch('/api/auth/sync-user', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include', // Important for session cookies
                body: JSON.stringify({
                    uid: user.uid,
                    email: user.email,
                    displayName: user.displayName || user.email.split('@')[0],
                    emailVerified: user.emailVerified,
                    providerId: providerId,
                    idToken: idToken  // Send ID token for server-side verification
                })
            });
            
            if (response.ok) {
                const data = await response.json();
                console.log('[FirebaseAuth] User synced successfully. Backend user ID:', data.userId);
                
                // Store in a global object for other scripts to access
                window.FirebaseAuthState = {
                    isAuthenticated: true,
                    firebaseUser: user,
                    backendUserId: data.userId,
                    email: user.email,
                    emailVerified: user.emailVerified,
                    syncedAt: Date.now()
                };
                
                // Dispatch custom event
                window.dispatchEvent(new CustomEvent('firebase-auth-synced', {
                    detail: window.FirebaseAuthState
                }));
                
                return true;
            } else if (response.status === 403) {
                // Handle email verification required
                const errorData = await response.json();
                if (errorData.code === 'EMAIL_NOT_VERIFIED') {
                    console.warn('[FirebaseAuth] Email not verified:', user.email);
                    const userEmail = user.email;
                    
                    // Check if user just signed up - if so, don't sign them out
                    // Let them stay signed in and see "Free Questions" prompt
                    // Check both window flag and sessionStorage (for page reloads)
                    const justSignedUpFlag = window.justSignedUp || sessionStorage.getItem('justSignedUp') === 'true';
                    if (justSignedUpFlag) {
                        console.log('[FirebaseAuth] User just signed up - keeping them signed in for Free Questions flow');
                        window.FirebaseAuthState = {
                            isAuthenticated: true,
                            firebaseUser: user,
                            backendUserId: null,
                            email: user.email,
                            emailVerified: false,
                            verificationRequired: true,
                            syncedAt: Date.now()
                        };
                        return true; // Return success - user stays signed in
                    }
                    
                    // Sign out the unverified user immediately (only for returning users logging in)
                    try {
                        const firebaseLib = typeof firebase !== 'undefined' ? firebase : null;
                        if (firebaseLib && firebaseLib.auth) {
                            await firebaseLib.auth().signOut();
                            console.log('[FirebaseAuth] Signed out unverified user:', userEmail);
                        }
                    } catch (signOutError) {
                        console.error('[FirebaseAuth] Error signing out unverified user:', signOutError);
                    }
                    
                    // Clear auth state
                    window.FirebaseAuthState = {
                        isAuthenticated: false,
                        firebaseUser: null,
                        backendUserId: null,
                        email: null,
                        emailVerified: false,
                        verificationRequired: true,
                        syncedAt: Date.now()
                    };
                    
                    // Dispatch event for email verification required
                    window.dispatchEvent(new CustomEvent('firebase-auth-verification-required', {
                        detail: {
                            email: userEmail,
                            message: errorData.message
                        }
                    }));
                    
                    return false;
                }
                console.error('[FirebaseAuth] Sync rejected:', errorData);
                return false;
            } else {
                console.error('[FirebaseAuth] Failed to sync with backend. Status:', response.status);
                const errorText = await response.text();
                console.error('[FirebaseAuth] Error response:', errorText);
                return false;
            }
        } catch (error) {
            console.error('[FirebaseAuth] Error syncing user:', error);
            return false;
        }
    }
    
    // Initialize authentication monitoring
    async function initializeAuth() {
        console.log('[FirebaseAuth] Initializing authentication handler...');
        
        const firebaseLib = await waitForFirebase();
        if (!firebaseLib) {
            console.error('[FirebaseAuth] Firebase not available, cannot initialize auth');
            return;
        }
        
        const auth = firebaseLib.auth();
        
        // Set up auth state listener (equivalent to onAuthStateChanged)
        auth.onAuthStateChanged(async (user) => {
            if (user) {
                console.log('[FirebaseAuth] User signed in:', user.uid, user.email);
                
                // Sync with backend immediately
                await syncUserWithBackend(user);
                
            } else {
                console.log('[FirebaseAuth] No user signed in');
                
                // Clear auth state
                window.FirebaseAuthState = {
                    isAuthenticated: false,
                    firebaseUser: null,
                    backendUserId: null,
                    email: null,
                    syncedAt: Date.now()
                };
                
                // Dispatch custom event
                window.dispatchEvent(new CustomEvent('firebase-auth-signout', {
                    detail: window.FirebaseAuthState
                }));
            }
        });
        
        // Check current auth state immediately
        const currentUser = auth.currentUser;
        if (currentUser) {
            console.log('[FirebaseAuth] Found current user on init:', currentUser.uid);
            await syncUserWithBackend(currentUser);
        }
    }
    
    // Public API
    window.FirebaseAuthHandler = {
        // Initialize the auth handler
        init: initializeAuth,
        
        // Get current auth state
        getAuthState: function() {
            return window.FirebaseAuthState || {
                isAuthenticated: false,
                firebaseUser: null,
                backendUserId: null,
                email: null,
                syncedAt: null
            };
        },
        
        // Check if user is authenticated
        isAuthenticated: function() {
            return window.FirebaseAuthState && window.FirebaseAuthState.isAuthenticated;
        },
        
        // Force sync current user
        syncCurrentUser: async function() {
            if (typeof firebase !== 'undefined' && firebase.auth) {
                const currentUser = firebase.auth().currentUser;
                if (currentUser) {
                    return await syncUserWithBackend(currentUser);
                }
            }
            return false;
        },
        
        // Wait for authentication to be ready
        waitForAuth: function(timeout = 5000) {
            return new Promise((resolve) => {
                // Check if already authenticated
                if (window.FirebaseAuthState && window.FirebaseAuthState.isAuthenticated) {
                    resolve(true);
                    return;
                }
                
                let resolved = false;
                
                // Listen for auth sync event
                const handleAuthSync = () => {
                    if (!resolved) {
                        resolved = true;
                        window.removeEventListener('firebase-auth-synced', handleAuthSync);
                        resolve(true);
                    }
                };
                
                window.addEventListener('firebase-auth-synced', handleAuthSync);
                
                // Timeout
                setTimeout(() => {
                    if (!resolved) {
                        resolved = true;
                        window.removeEventListener('firebase-auth-synced', handleAuthSync);
                        resolve(false);
                    }
                }, timeout);
            });
        }
    };
    
    // Auto-initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializeAuth);
    } else {
        // DOM already loaded
        initializeAuth();
    }
    
    console.log('[FirebaseAuth] Firebase authentication handler loaded');
})();